import { supabase } from '../lib/supabase'
import { logCreate, logDelete } from './userLogs'
import type { SupportCenterAppointmentAttachment } from '../types/supportCenterAppointments'
import {
  SUPPORT_CENTER_ATTACHMENT_BUCKET,
  SUPPORT_CENTER_ATTACHMENT_EXTENSIONS,
  SUPPORT_CENTER_ATTACHMENT_MAX_BYTES,
  SUPPORT_CENTER_ATTACHMENT_MAX_MB,
  SUPPORT_CENTER_ATTACHMENT_MIME_BY_EXT,
} from '../types/supportCenterAppointments'

const SIGNED_URL_EXPIRES_IN = 3600

function fileExtension(fileName: string): string {
  const idx = fileName.lastIndexOf('.')
  if (idx < 0) return ''
  return fileName.slice(idx).toLowerCase()
}

function sanitizeFileName(fileName: string): string {
  return fileName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 120)
}

function resolveContentType(file: File): string {
  const ext = fileExtension(file.name)
  if (file.type && Object.values(SUPPORT_CENTER_ATTACHMENT_MIME_BY_EXT).includes(file.type)) {
    return file.type
  }
  return SUPPORT_CENTER_ATTACHMENT_MIME_BY_EXT[ext] || file.type || 'application/octet-stream'
}

function assertAllowedFile(file: File): void {
  const ext = fileExtension(file.name)
  if (
    !(SUPPORT_CENTER_ATTACHMENT_EXTENSIONS as readonly string[]).includes(ext)
  ) {
    throw new Error(
      `O arquivo "${file.name}" não é permitido. Use doc, docx, pdf ou imagens (jpg, png, gif, webp).`
    )
  }
  if (file.size > SUPPORT_CENTER_ATTACHMENT_MAX_BYTES) {
    throw new Error(
      `O arquivo "${file.name}" excede o limite de ${SUPPORT_CENTER_ATTACHMENT_MAX_MB} MB.`
    )
  }
}

export const supportCenterAppointmentAttachmentsService = {
  async listByAppointment(appointmentId: string): Promise<SupportCenterAppointmentAttachment[]> {
    if (!appointmentId.trim()) return []
    const { data, error } = await supabase
      .from('support_center_appointment_attachments')
      .select('*')
      .eq('appointment_id', appointmentId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true })
    if (error) throw error
    return (data || []) as SupportCenterAppointmentAttachment[]
  },

  async createSignedUrl(filePath: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from(SUPPORT_CENTER_ATTACHMENT_BUCKET)
      .createSignedUrl(filePath, SIGNED_URL_EXPIRES_IN)
    if (error) throw error
    return data.signedUrl
  },

  /**
   * Faz upload de um ou mais arquivos na pasta do atendimento.
   * Em falha parcial, remove do bucket o que já subiu nesta tentativa.
   */
  async upload(
    appointmentId: string,
    files: File[]
  ): Promise<SupportCenterAppointmentAttachment[]> {
    if (!appointmentId.trim()) throw new Error('Agendamento não identificado.')
    if (!files.length) return []

    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')

    for (const file of files) {
      assertAllowedFile(file)
    }

    const uploadedPaths: string[] = []
    const rows: SupportCenterAppointmentAttachment[] = []

    try {
      for (const file of files) {
        const ext = fileExtension(file.name)
        const safeName = sanitizeFileName(file.name) || `arquivo${ext}`
        const filePath = `${appointmentId}/${crypto.randomUUID()}_${safeName}`
        const contentType = resolveContentType(file)

        const { error: storageError } = await supabase.storage
          .from(SUPPORT_CENTER_ATTACHMENT_BUCKET)
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false,
            contentType,
          })
        if (storageError) throw storageError
        uploadedPaths.push(filePath)

        const { data, error: dbError } = await supabase
          .from('support_center_appointment_attachments')
          .insert({
            appointment_id: appointmentId,
            file_path: filePath,
            file_name: file.name,
            file_size: file.size,
            mime_type: contentType,
            uploaded_by: user.id,
          })
          .select()
          .single()

        if (dbError) throw dbError
        const row = data as SupportCenterAppointmentAttachment
        rows.push(row)
        void logCreate(
          'support_center_appointment_attachment',
          row.id,
          `Central de Amparo: anexou "${row.file_name}" ao agendamento`,
          {
            appointment_id: appointmentId,
            file_name: row.file_name,
          }
        )
      }

      return rows
    } catch (err) {
      if (uploadedPaths.length > 0) {
        try {
          await supabase.storage.from(SUPPORT_CENTER_ATTACHMENT_BUCKET).remove(uploadedPaths)
        } catch {
          /* best-effort cleanup */
        }
        try {
          await supabase
            .from('support_center_appointment_attachments')
            .delete()
            .in('file_path', uploadedPaths)
        } catch {
          /* best-effort cleanup */
        }
      }
      throw err
    }
  },

  /**
   * Exclusão lógica do anexo (preenche deleted_at). Mantém o arquivo no storage.
   */
  async remove(attachment: SupportCenterAppointmentAttachment): Promise<void> {
    if (!attachment?.id?.trim()) throw new Error('Anexo não identificado.')

    const deletedAt = new Date().toISOString()
    const { data, error: dbError } = await supabase
      .from('support_center_appointment_attachments')
      .update({ deleted_at: deletedAt })
      .eq('id', attachment.id)
      .is('deleted_at', null)
      .select('id')
      .maybeSingle()
    if (dbError) throw dbError
    if (!data?.id) {
      throw new Error('Anexo não encontrado ou já removido.')
    }

    void logDelete(
      'support_center_appointment_attachment',
      attachment.id,
      `Central de Amparo: removeu anexo "${attachment.file_name}" do agendamento`,
      {
        appointment_id: attachment.appointment_id,
        file_name: attachment.file_name,
        deleted_at: deletedAt,
      }
    )
  },
}
